import React, { useRef, useState, useEffect } from 'react';
import { useFormDepartureContext } from '../../../components/FormDepartureContext';
import { SNL_BRAND } from '../../../data/map';

const ALLOWED = ['jpg', 'jpeg', 'png', 'gif'];
const ACCEPT = '.jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif';

const PhotoSection = () => {
  const { formData, formErrors, modifiedFields, setFormErrors, setFile, setModifiedFields, setFieldValue, brand } = useFormDepartureContext();
  const ACCENT = (brand || SNL_BRAND).accent;

  const inputRef = useRef(null);
  const [photos, setPhotos] = useState(formData.photos || []);
  const [dragOver, setDragOver] = useState(false);
  const [rejected, setRejected] = useState([]);

  useEffect(() => {
    if (!formData.photos) setFieldValue('photos', []);
    setPhotos(formData.photos || []);
  }, [formData.photos, setFieldValue]);

  // Object URLs for thumbnails — revoke when a photo goes away
  useEffect(() => () => photos.forEach((p) => p.preview && URL.revokeObjectURL(p.preview)), []); // eslint-disable-line

  const syncPhotos = (updated) => {
    setPhotos(updated);
    setFieldValue('photos', updated);
    setFile('photoUpload', updated.length > 0 ? updated[0].file : null);   // backward-compat single field
    setModifiedFields((prev) => new Set([...prev, 'photos', 'photoUpload']));
    if (updated.length > 0) {
      setFormErrors((prev) => { const n = { ...prev }; delete n.photos; delete n.photoUpload; return n; });
    }
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const bad = [];
    const good = [];
    incoming.forEach((file) => {
      const ext = file.name.split('.').pop().toLowerCase();
      const dup = photos.some((p) => p.name === file.name && p.file?.size === file.size);
      if (!ALLOWED.includes(ext)) bad.push(`${file.name} — only jpg, png or gif`);
      else if (dup) bad.push(`${file.name} — already added`);
      else good.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, file, name: file.name, preview: URL.createObjectURL(file) });
    });
    setRejected(bad);
    if (good.length) syncPhotos([...photos, ...good]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const removePhoto = (id) => {
    const target = photos.find((p) => p.id === id);
    if (target?.preview) URL.revokeObjectURL(target.preview);
    syncPhotos(photos.filter((p) => p.id !== id));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  return (
    <div className="form-section">
      <h4 className="ob-record-title" style={{ marginBottom: 6 }}>Upload Ticket Photos</h4>
      <p className="ob-subtitle" style={{ marginBottom: 14 }}>
        At least one photo is required. Drop several at once — large photos are shrunk automatically when you submit.
      </p>

      {/* ── Drop zone ── */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{
          border: `2px dashed ${dragOver ? ACCENT : `${ACCENT}66`}`,
          borderRadius: 14,
          padding: '34px 20px',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragOver ? `${ACCENT}14` : 'rgba(255,255,255,0.02)',
          transition: 'all .15s ease',
        }}
      >
        <input ref={inputRef} type="file" accept={ACCEPT} multiple style={{ display: 'none' }}
          onChange={(e) => addFiles(e.target.files)} />
        <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="1.8" style={{ margin: '0 auto 8px', display: 'block' }}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 9l5-5 5 5M12 4v12" />
        </svg>
        <div style={{ color: '#e2e8f0', fontWeight: 700, fontSize: '0.95rem' }}>
          {dragOver ? 'Drop to add' : 'Drag & drop photos here'}
        </div>
        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem', marginTop: 6 }}>
          or click to browse · jpg, png, gif · multiple files at once
        </div>
      </div>

      {/* ── Rejected files ── */}
      {rejected.length > 0 && (
        <div className="ob-error" style={{ marginTop: 10, flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
          {rejected.map((r, i) => <span key={i}>⚠ {r}</span>)}
        </div>
      )}

      {/* ── Thumbnails ── */}
      {photos.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginTop: 16 }}>
          {photos.map((photo, index) => (
            <div key={photo.id} style={{ position: 'relative', borderRadius: 12, overflow: 'hidden',
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ aspectRatio: '4 / 3', background: '#0b1220' }}>
                {photo.preview ? (
                  <img src={photo.preview} alt={photo.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                    {photo.name}
                  </div>
                )}
              </div>
              <span style={{ position: 'absolute', top: 6, left: 6, width: 22, height: 22, borderRadius: '50%',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: ACCENT, color: '#0f172a', fontWeight: 800, fontSize: '0.7rem' }}>
                {index + 1}
              </span>
              <button type="button" onClick={(e) => { e.stopPropagation(); removePhoto(photo.id); }}
                title="Remove photo" aria-label={`Remove ${photo.name}`}
                style={{ position: 'absolute', top: 6, right: 6, width: 24, height: 24, borderRadius: '50%',
                  border: '1px solid rgba(248,113,113,0.6)', background: 'rgba(15,23,42,0.85)', color: '#fca5a5',
                  fontSize: '0.95rem', lineHeight: 1, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                ×
              </button>
              <div style={{ padding: '6px 8px', fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={photo.name}>
                {photo.name}
                <span style={{ color: 'rgba(255,255,255,0.4)' }}> · {photo.file ? fmtSize(photo.file.size) : ''}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {photos.length === 0 && !formErrors.photos && (
        <p className="ob-subtitle" style={{ textAlign: 'center', marginTop: 12, color: '#fbbf24' }}>No photos added yet.</p>
      )}
      {formErrors.photoUpload && modifiedFields?.has?.('photoUpload') && <div className="ob-error" style={{ marginTop: 10 }}>{formErrors.photoUpload}</div>}
      {formErrors.photos && <div className="ob-error" style={{ marginTop: 10 }}>{formErrors.photos}</div>}

      {photos.length > 0 && (
        <p className="ob-subtitle" style={{ textAlign: 'center', marginTop: 12 }}>
          {photos.length} {photos.length === 1 ? 'photo' : 'photos'} ready
        </p>
      )}
    </div>
  );
};

export default PhotoSection;